import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Heart, Award, Globe, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const About = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Heart,
      titleKey: 'medicalExpertise',
      title: 'Medical Expertise',
      titleAr: 'الخبرة الطبية',
      description: 'Founded by medical professionals with over 15 years of clinical practice and healthcare leadership experience.',
      descriptionAr: 'تأسست على يد متخصصين طبيين يمتلكون أكثر من 15 عاماً من الخبرة في الممارسة السريرية والقيادة الصحية.'
    },
    {
      icon: Award,
      titleKey: 'qualityFocus',
      title: 'Quality Focus',
      titleAr: 'التركيز على الجودة',
      description: 'Committed to transparency and quality in healthcare recruitment. No shortcuts, no misleading claims.',
      descriptionAr: 'ملتزمون بالشفافية والجودة في توظيف الرعاية الصحية. لا اختصارات ولا ادعاءات مضللة.'
    },
    {
      icon: Globe,
      titleKey: 'regionalFocus',
      title: 'Regional Focus',
      titleAr: 'التركيز الإقليمي',
      description: 'Designed to connect international medical talent with opportunities across Saudi Arabia and the Middle East.',
      descriptionAr: 'مصممة لربط الكفاءات الطبية الدولية بالفرص في المملكة العربية السعودية والشرق الأوسط.'
    },
    {
      icon: Users,
      titleKey: 'humanCentred',
      title: 'Human-Centred',
      titleAr: 'التركيز على الإنسان',
      description: 'Technology enables us, but people are at the heart of everything we do.',
      descriptionAr: 'التكنولوجيا تمكننا، لكن الناس هم في صميم كل ما نقوم به.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('about.title')}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>

          {/* Founder Section */}
          <section className="mb-20">
            <div className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10 rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{t('about.storyTitle')}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {t('about.storyText')}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {t('about.storyText2') || 'Having experienced firsthand the challenges of international medical recruitment—outdated processes, lengthy hiring times, and candidates struggling with complex relocation requirements—Luma Med Global was created to address these challenges.'}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t('about.storyText3') || 'We are an early-stage platform focused on building a quality talent pool and connecting healthcare professionals with opportunities in Saudi Arabia and the Middle East, supporting the region\'s Vision 2030 healthcare transformation.'}
              </p>
            </div>
          </section>

          {/* Values */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">{t('about.valuesTitle')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {values.map((value, index) => (
                <Card key={index} className="border-2 hover:border-[#42b1c2] transition-all hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-[#42b1c2]/10 rounded-xl flex items-center justify-center mb-4">
                      <value.icon className="h-7 w-7 text-[#42b1c2]" />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                      {t(`about.${value.titleKey}Title`) !== `about.${value.titleKey}Title` 
                        ? t(`about.${value.titleKey}Title`) 
                        : value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t(`about.${value.titleKey}Desc`) !== `about.${value.titleKey}Desc`
                        ? t(`about.${value.titleKey}Desc`)
                        : value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* What We Offer */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">{t('about.whatWeOfferTitle') || 'What We Offer'}</h2>
            <div className="bg-white border-2 border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('about.offer1Title') || 'Founded by a Medical Professional'}</h3>
                  <p className="text-gray-600">
                    {t('about.offer1Desc') || 'With over 15 years of clinical and healthcare leadership experience, we understand the medical field from the inside—the competencies that matter, the challenges professionals face, and what healthcare employers need.'}
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('about.offer2Title') || 'Structured Recruitment Workflows'}</h3>
                  <p className="text-gray-600">
                    {t('about.offer2Desc') || 'We provide organised document collection, video interview capabilities, and scheduling tools designed to support efficient recruitment processes.'}
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('about.offer3Title') || 'Information and Guidance'}</h3>
                  <p className="text-gray-600">
                    {t('about.offer3Desc') || 'Resources to help candidates understand SCFHS requirements, visa processes, and relocation considerations for working in Saudi Arabia.'}
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('about.offer4Title') || 'Transparent Approach'}</h3>
                  <p className="text-gray-600">
                    {t('about.offer4Desc') || 'We believe in honest communication. As an early-stage platform, we\'re building our network of candidates and employers with integrity and transparency.'}
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('about.offer5Title') || 'Vision 2030 Alignment'}</h3>
                  <p className="text-gray-600">
                    {t('about.offer5Desc') || 'We aim to contribute to Saudi Arabia\'s healthcare transformation by helping connect qualified international medical professionals with opportunities in the Kingdom.'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Commitment Section */}
          <section>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">{t('about.commitmentTitle') || 'Our Commitment'}</h2>
            <div className="bg-gradient-to-r from-[#42b1c2] to-[#3a9fb0] text-white rounded-3xl p-8 md:p-12 text-center">
              <p className="text-xl md:text-2xl leading-relaxed">
                {t('about.commitmentText') || 'Built with transparency, safety, and long-term healthcare workforce quality in mind. Every candidate deserves a clear process, and every employer deserves honest information. That\'s what we\'re committed to delivering.'}
              </p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default About;
